import { EmptyState, SectionCard } from '@atlas/ui-components'
import { cn } from '@atlas/ui-primitives'
import { AssetGridSkeleton } from '../asset-tile/asset-grid-skeleton'
import { ASSET_TILE_GRID, AssetThumbTile } from '../asset-tile/asset-thumb-tile'
import { channelInForce, channelPillsFor, deliverablesOn } from './deliverable-channels'
import type { DeliverableItem } from './deliverables-card.types'

/** The card's default heading. Shared with a host's own skeleton so the two cannot diverge. */
export const DELIVERABLES_TITLE = 'Delivered assets'

/**
 * The grid's body while the deliverables are being read: a plausible screenful of tiles, so
 * the tallest section on a page does not grow under the reader as they arrive.
 *
 * Exported so a host's own first-read skeleton stands the grid in at the same size this card
 * does - the tile count is a guess either way, and two different guesses would make the page
 * settle twice.
 */
export function DeliverablesBodySkeleton({ label }: { label?: string }) {
	return <AssetGridSkeleton count={12} label={label} />
}

export interface DeliverablesCardProps {
	deliverables: DeliverableItem[]
	/** The reader's channel pick; ignored while the deliverables carry no such channel. */
	channel: string
	onChannelChange: (channel: string) => void
	onOpenDeliverable: (deliverableId: string) => void
	/** Overrides the section heading, e.g. `Related deliverables` on another surface. */
	title?: string
	/** What an empty result means here - the host knows what it narrowed by. */
	emptyLabel?: string
	isLoading?: boolean
	isError?: boolean
}

/**
 * The deliverables produced for whatever the host resolved for, laid out as a tile grid and
 * filterable by the channel each one is cut for.
 *
 * Deliberately NOT a filename-and-metadata card: a tile here is a small square whose only
 * label is its marker, because what distinguishes one delivered cut from another is its shape
 * rather than its name. A surface that needs the metadata form composes {@link AssetThumbTile}
 * itself instead of asking this card for a variant that switches everything about it.
 *
 * A deliverable with no channel is filtered out of every specific pill but stays under "All" -
 * dropping it would report a classification gap as absence.
 *
 * The channel pick is honoured only while the deliverables still offer it (see
 * `channelInForce`), so the card can never sit empty with nothing selected. That is also why
 * there is no "nothing on this channel" state: the pills and the visible list derive from the
 * same deliverables, so a pill exists only when something carries it.
 */
export function DeliverablesCard({
	deliverables,
	channel,
	onChannelChange,
	onOpenDeliverable,
	title = DELIVERABLES_TITLE,
	emptyLabel = 'No delivered assets here.',
	isLoading,
	isError,
}: DeliverablesCardProps) {
	const pills = channelPillsFor(deliverables)
	const active = channelInForce(deliverables, channel)
	const visible = deliverablesOn(deliverables, active)

	return (
		<SectionCard
			title={title}
			count={isLoading || isError ? undefined : deliverables.length}
			action={
				pills.length > 1 ? (
					<div role="group" aria-label="Filter by channel" className="flex flex-wrap gap-1.5">
						{pills.map((pill) => (
							<button
								key={pill.code}
								type="button"
								aria-pressed={active === pill.code}
								onClick={() => onChannelChange(pill.code)}
								className={cn(
									'rounded-ds-full px-3 py-1 text-ds-xs leading-4 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-ring',
									active === pill.code
										? 'bg-ds-primary text-ds-primary-foreground'
										: 'border border-ds-border bg-ds-surface text-ds-text hover:border-ds-text',
								)}
							>
								{pill.label}
							</button>
						))}
					</div>
				) : null
			}
		>
			{isLoading ? (
				<DeliverablesBodySkeleton label="Loading assets…" />
			) : isError ? (
				<p className="text-ds-sm leading-5 text-ds-text-muted">
					Could not load the deliverables for this context. Please retry.
				</p>
			) : deliverables.length === 0 ? (
				<EmptyState title={emptyLabel} />
			) : (
				<div className={ASSET_TILE_GRID}>
					{visible.map((deliverable) => (
						<AssetThumbTile
							key={deliverable.id}
							fileName={deliverable.name}
							contentType={deliverable.contentType}
							previewUrl={deliverable.previewUrl}
							marker={deliverable.marker}
							onClick={() => onOpenDeliverable(deliverable.id)}
						/>
					))}
				</div>
			)}
		</SectionCard>
	)
}
